import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import Box from '@mui/material/Box';
import Drawer from '@mui/material/Drawer';
import LinearProgress from '@mui/material/LinearProgress';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useTheme } from '@mui/material/styles';
import MarkdownRenderer from './MarkdownRenderer';
import ErrorBoundary from './ErrorBoundary';
import TableOfContents from '../navigation/TableOfContents';
import DocumentNavigation from '../navigation/DocumentNavigation';
import DocumentSearch from '../search/DocumentSearch';
import ViewerToolbar from '../toolbar/ViewerToolbar';
import { useDebouncedValue } from '../../hooks/useDebouncedValue';
import { resolveViewerOptions } from '../../config/viewerOptions';
import { DEFAULT_READING_WIDTH, READING_WIDTHS, STORAGE_KEYS, type ReadingWidth } from '../../config/defaults';
import type { Heading, MarkdownViewerProps } from '../../types';

const DRAWER_WIDTH = 300;

export interface MarkdownViewerFullProps extends MarkdownViewerProps {
  /** Document title shown in the toolbar. */
  title?: string;
  subtitle?: string;
  fileName?: string;
  /** Set false to embed just the rendered document with no chrome. */
  showToolbar?: boolean;
  showNavigation?: boolean;
  loading?: boolean;
  onOpenFile?: () => void;
  onGoHome?: () => void;
}

const readStoredWidth = (): ReadingWidth => {
  try {
    const stored = window.localStorage.getItem(STORAGE_KEYS.readingWidth);
    return stored && stored in READING_WIDTHS ? (stored as ReadingWidth) : DEFAULT_READING_WIDTH;
  } catch {
    return DEFAULT_READING_WIDTH;
  }
};

/**
 * The complete document viewer: toolbar, contents, find-in-page and the
 * rendered Markdown.
 *
 * Callers only supply Markdown and feature switches -- nothing about the parsing
 * pipeline leaks into this API.
 */
export const MarkdownViewer = ({
  content,
  options,
  baseUrl,
  className,
  onHeadingsChange,
  title = 'Markdown Viewer',
  subtitle,
  fileName = 'document.md',
  showToolbar = true,
  showNavigation = true,
  loading = false,
  onOpenFile,
  onGoHome,
}: MarkdownViewerFullProps) => {
  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up('lg'));
  const resolved = useMemo(() => resolveViewerOptions(options), [options]);

  const [headings, setHeadings] = useState<Heading[]>([]);
  const [tocOpen, setTocOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [matchCount, setMatchCount] = useState(0);
  const [activeIndex, setActiveIndex] = useState(0);
  const [readingWidth, setReadingWidth] = useState<ReadingWidth>(DEFAULT_READING_WIDTH);

  const contentRef = useRef<HTMLDivElement>(null);
  const debouncedQuery = useDebouncedValue(query);

  useEffect(() => setReadingWidth(readStoredWidth()), []);
  useEffect(() => setTocOpen(isDesktop && resolved.enableToc), [isDesktop, resolved.enableToc]);
  useEffect(() => setActiveIndex(0), [debouncedQuery]);

  const handleHeadings = useCallback(
    (next: Heading[]) => {
      setHeadings(next);
      onHeadingsChange?.(next);
    },
    [onHeadingsChange],
  );

  const changeReadingWidth = useCallback((width: ReadingWidth) => {
    setReadingWidth(width);
    try {
      window.localStorage.setItem(STORAGE_KEYS.readingWidth, width);
    } catch {
      /* preference is session-only when storage is unavailable */
    }
  }, []);

  // Ctrl/Cmd+F opens the in-document find bar instead of the browser's.
  useEffect(() => {
    if (!resolved.enableSearch) return;

    const onKeyDown = (event: KeyboardEvent) => {
      if ((event.ctrlKey || event.metaKey) && event.key.toLowerCase() === 'f') {
        event.preventDefault();
        setSearchOpen(true);
      }
    };

    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [resolved.enableSearch]);

  // Move the "current match" marker and bring it into view.
  useEffect(() => {
    const root = contentRef.current;
    if (!root) return;

    const hits = root.querySelectorAll<HTMLElement>('.search-hit');
    hits.forEach((hit, index) => hit.setAttribute('data-active', String(index === activeIndex)));
    hits[activeIndex]?.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }, [activeIndex, matchCount, debouncedQuery]);

  // Honour a #fragment present on first load, once the document has rendered.
  useEffect(() => {
    if (!window.location.hash || headings.length === 0) return;
    const id = decodeURIComponent(window.location.hash.slice(1));
    const target = document.getElementById(id);
    if (target) target.scrollIntoView({ block: 'start' });
  }, [headings.length]);

  const closeSearch = useCallback(() => {
    document.querySelector<HTMLButtonElement>('.toolbar-search-trigger')?.focus();
    setSearchOpen(false);
    setQuery('');
  }, []);

  const maxWidth = READING_WIDTHS[readingWidth] === 0 ? '100%' : `${READING_WIDTHS[readingWidth]}ch`;
  const tocAvailable = resolved.enableToc && headings.length > 0;

  const toc = (
    <TableOfContents headings={headings} onNavigate={() => !isDesktop && setTocOpen(false)} />
  );

  return (
    <Box className={['markdown-viewer', className].filter(Boolean).join(' ')} sx={{ minHeight: '100vh', bgcolor: 'background.paper' }}>
      {showToolbar && (
        <ViewerToolbar
          title={title}
          subtitle={subtitle}
          content={content}
          fileName={fileName}
          tocOpen={tocOpen}
          tocAvailable={tocAvailable}
          onToggleToc={() => setTocOpen((open) => !open)}
          searchOpen={searchOpen}
          searchAvailable={resolved.enableSearch}
          onToggleSearch={() => (searchOpen ? closeSearch() : setSearchOpen(true))}
          readingWidth={readingWidth}
          onReadingWidthChange={changeReadingWidth}
          onOpenFile={onOpenFile}
          onGoHome={onGoHome}
        />
      )}

      {loading && <LinearProgress sx={{ position: 'sticky', top: 64, zIndex: 2 }} />}

      <Box sx={{ display: 'flex', alignItems: 'flex-start' }}>
        {tocAvailable && (
          <Drawer
            variant={isDesktop ? 'persistent' : 'temporary'}
            open={tocOpen}
            onClose={() => setTocOpen(false)}
            ModalProps={{ keepMounted: true }}
            sx={{
              width: tocOpen ? DRAWER_WIDTH : 0,
              flexShrink: 0,
              transition: theme.transitions.create('width', { duration: 200 }),
              '& .MuiDrawer-paper': {
                width: DRAWER_WIDTH,
                boxSizing: 'border-box',
                top: { lg: showToolbar ? 65 : 0 },
                height: { lg: showToolbar ? 'calc(100% - 65px)' : '100%' },
                borderRight: `1px solid ${theme.palette.divider}`,
                backgroundColor: theme.palette.background.default,
              },
            }}
          >
            {toc}
          </Drawer>
        )}

        <Box
          component="main"
          id="main-content"
          tabIndex={-1}
          sx={{
            flex: 1,
            minWidth: 0,
            px: { xs: 2, sm: 4, lg: 6 },
            py: { xs: 3, sm: 4 },
          }}
        >
          <Box sx={{ maxWidth, mx: 'auto' }}>
            {resolved.enableSearch && (
              <Box
                sx={{
                  position: 'sticky',
                  top: showToolbar ? 76 : 12,
                  zIndex: 3,
                  mb: searchOpen ? 2 : 0,
                }}
              >
                <DocumentSearch
                  open={searchOpen}
                  query={query}
                  matchCount={matchCount}
                  activeIndex={activeIndex}
                  onQueryChange={setQuery}
                  onActiveIndexChange={setActiveIndex}
                  onClose={closeSearch}
                />
              </Box>
            )}

            <Box ref={contentRef}>
              <ErrorBoundary>
                <MarkdownRenderer
                  content={content}
                  options={resolved}
                  baseUrl={baseUrl}
                  searchTerm={searchOpen ? debouncedQuery : ''}
                  onHeadingsChange={handleHeadings}
                  onMatchCountChange={setMatchCount}
                />
              </ErrorBoundary>
            </Box>

            {showNavigation && <DocumentNavigation headings={headings} />}
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default MarkdownViewer;
